/** Class calendar (CS-01): weekly grid of class_sessions with live booked/cap
 *  fill badge, instructor + room, and a click handler to open the edit modal. */
import type { types } from '@gym/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn, Spinner } from '../../components/ui.js';

import {
  type ClassTemplate,
  useClassSessions,
  useClassTemplates,
} from './hooks/useClassSessions.js';

const DAY_MS = 86_400_000;

function startOfWeek(d: Date): Date {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Monday = 0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - dow);
  return x;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export interface ClassCalendarProps {
  weekStart?: Date;
  onlyPt?: boolean;
  instructorStaffId?: string;
  onSessionClick?: (session: types.ClassSessionDTO) => void;
}

export function ClassCalendar({
  weekStart,
  onlyPt,
  instructorStaffId,
  onSessionClick,
}: ClassCalendarProps): ReactNode {
  const { t } = useTranslation();
  const start = startOfWeek(weekStart ?? new Date());
  const end = new Date(start.getTime() + 7 * DAY_MS);

  const { data: sessions, isLoading } = useClassSessions({
    from: start.toISOString(),
    to: end.toISOString(),
    ...(onlyPt !== undefined ? { onlyPt } : {}),
    ...(instructorStaffId ? { instructorStaffId } : {}),
  });
  const { data: templates } = useClassTemplates();
  const classMap = new Map<string, ClassTemplate>((templates ?? []).map((c) => [c.id, c]));

  const days = Array.from({ length: 7 }, (_, i) => new Date(start.getTime() + i * DAY_MS));
  const today = new Date().toDateString();

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <div role="grid" aria-label={t('classes.calendar.title')} className="grid grid-cols-7">
        {days.map((d) => {
          const isToday = d.toDateString() === today;
          return (
            <div
              key={d.toISOString()}
              role="columnheader"
              className={cn(
                'border-b border-l border-border px-2 py-2.5 text-center text-xs font-semibold first:border-l-0',
                isToday ? 'text-primary' : 'text-muted',
              )}
            >
              {d.toLocaleDateString([], { weekday: 'short' })}{' '}
              <span className="tnum">{d.getDate()}</span>
            </div>
          );
        })}

        {isLoading ? (
          <div className="col-span-7 grid place-items-center py-16">
            <Spinner className="h-6 w-6 text-primary" />
          </div>
        ) : (
          days.map((d) => {
            const dayStr = d.toDateString();
            const daySessions = (sessions ?? []).filter(
              (s) => new Date(s.startsAt).toDateString() === dayStr,
            );
            return (
              <div
                key={`col-${d.toISOString()}`}
                role="gridcell"
                className="min-h-[12rem] space-y-1.5 border-l border-border p-1.5 first:border-l-0"
              >
                {daySessions.map((s) => {
                  const full = s.bookedCount >= s.capacity;
                  const cancelled = s.status === 'cancelled';
                  const accent = cancelled
                    ? 'border-l-faint'
                    : full
                      ? 'border-l-danger'
                      : 'border-l-primary';
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`session-${s.id}`}
                      onClick={() => onSessionClick?.(s)}
                      className={cn(
                        'w-full rounded-lg border border-l-2 border-border bg-surface-2 px-2 py-1.5 text-left transition-colors',
                        'hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                        accent,
                        cancelled && 'opacity-60',
                      )}
                    >
                      <div
                        className={cn(
                          'truncate text-xs font-semibold text-fg',
                          cancelled && 'line-through',
                        )}
                      >
                        {classMap.get(s.classId ?? '')?.name ?? '—'}
                      </div>
                      <div className="text-[11px] text-muted tnum">{fmtTime(s.startsAt)}</div>
                      <div className="mt-0.5 text-[11px]">
                        {full ? (
                          <span data-testid={`full-${s.id}`} className="font-semibold text-danger">
                            {t('classes.full')}
                          </span>
                        ) : (
                          <span className="text-muted tnum">
                            {s.bookedCount}/{s.capacity}
                          </span>
                        )}
                        {s.waitlistCount > 0 ? (
                          <span className="text-info"> · +{s.waitlistCount}</span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
